import KeyboardArrowDown from "@material-ui/icons/KeyboardArrowDown";
import KeyboardArrowUp from "@material-ui/icons/KeyboardArrowUp";
import { isAuthTokenExpired } from "@saleor/auth";
import {
  DateSelectionType,
  FIELD_TYPE,
  orderTypeChoices,
  SubmitFilterDataType
} from "@saleor/changeOrderExport/components/OrderFilterSection/constant";
import {
  ChangeOrderStatusEnum,
  ScgpExportOrderStatus,
  useDropdownListMaterialGroup1Query,
  useFilterRequireAttentionMaterialLazyQuery,
  useFilterSaleEmployeeDomesticOrderLazyQuery,
  useFilterSoldToDomesticOrderLazyQuery,
  useListOrderTypesQuery,
  useListSourceOfAppQuery,
  useScgpMeQuery
} from "@saleor/graphql";
import { useFormik } from "formik";
import uniqBy from "lodash/uniqBy";
import moment from "moment-timezone";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";

import { getOrderTypes, getProductGroup } from "./utils";

const useOrderFilterDomestic = ({ handleChangeFilter }) => {
  const intl = useIntl();
  const { data: dataScgpMe } = useScgpMeQuery({
    skip: isAuthTokenExpired()
  });

  const { data: productGroup } = useDropdownListMaterialGroup1Query({
    variables: { bu: "PP" }
  });

  const formik = useFormik({
    initialValues: {
      soldTo: "",
      materialCodeName: "",
      soNo: "",
      contractNo: "",
      dpNo: "",
      poNo: "",
      invoiceNo: "",
      bu: "",
      company: "",
      saleGroup: "All",
      saleEmployee: "",
      orderType: "",
      statusSAP: "",
      dateSelectionType: DateSelectionType.CREATE_DATE,
      gte: null,
      lte: null,
      createDate: null,
      lastUpdate: null,
      materialGroup1: "All",
      sourceOfApp: "All"
    },
    onSubmit: data => {
      const {
        soldTo,
        poNo,
        materialCodeName,
        soNo,
        contractNo,
        saleGroup,
        bu,
        company,
        statusSAP,
        saleEmployee,
        orderType,
        dpNo,
        invoiceNo,
        sourceOfApp
      } = data;
      if (
        !materialCodeName &&
        !soNo &&
        !poNo &&
        !saleGroup &&
        !statusSAP &&
        !bu &&
        !company &&
        !soldTo &&
        !contractNo &&
        !orderType &&
        !dpNo &&
        !invoiceNo &&
        !saleEmployee &&
        !sourceOfApp
      ) {
        formik.setErrors({
          soldTo: "กรุณากรอกข้อมูล",
          poNo: "กรุณากรอกข้อมูล",
          materialCodeName: "กรุณากรอกข้อมูล",
          soNo: "กรุณากรอกข้อมูล",
          contractNo: "กรุณากรอกข้อมูล",
          saleGroup: "กรุณากรอกข้อมูล",
          bu: "กรุณากรอกข้อมูล",
          company: "กรุณากรอกข้อมูล",
          statusSAP: "กรุณากรอกข้อมูล",
          saleEmployee: "กรุณากรอกข้อมูล",
          orderType: "กรุณากรอกข้อมูล",
          invoiceNo: "กรุณากรอกข้อมูล",
          dpNo: "กรุณากรอกข้อมูล"
        });
      } else {
        const newData = { ...data };
        delete newData.statusSAP;
        delete newData.gte;
        delete newData.lte;

        const submitData: SubmitFilterDataType = { ...newData };
        delete submitData.dateSelectionType;

        submitData.statusSap = data.statusSAP as ChangeOrderStatusEnum;
        // data.statusSAP === "ALL"
        //   ? [ChangeOrderStatusEnum.COMPLETE, ChangeOrderStatusEnum.OPEN]
        //   : data.statusSAP
        //   ? [
        //       data.statusSAP === ChangeOrderStatusEnum.COMPLETE
        //         ? ChangeOrderStatusEnum.COMPLETE
        //         : ChangeOrderStatusEnum.OPEN
        //     ]
        //   : [];
        for (const [key, value] of Object.entries(submitData)) {
          if (!value) {
            delete submitData[key];
          }
        }
        const dateRange = {
          gte: data.gte ? moment(data.gte).format("YYYY-MM-DD") : undefined,
          lte: data.lte ? moment(data.lte).format("YYYY-MM-DD") : undefined
        };
        if (data.dateSelectionType === DateSelectionType.CREATE_DATE) {
          delete submitData.lastUpdate;
          submitData.createDate = dateRange;
        } else {
          delete submitData.createDate;
          submitData.lastUpdate = dateRange;
        }
        handleChangeFilter(submitData);
      }
    },
    enableReinitialize: true
  });

  const [isOpen, setIsOpen] = useState<boolean>(true);
  const [isClean, setIsClean] = useState<boolean>(false);

  const [
    getSoldToDataSuggestions,
    { data: fetchSoldToDataSuggestions, loading: loadingSoldToDataSuggestion }
  ] = useFilterSoldToDomesticOrderLazyQuery();

  const [
    getSaleEmployeeDataSuggestions,
    {
      data: fetchSaleEmployeeDataSuggestions,
      loading: loadingSaleEmployeeDataSuggestion
    }
  ] = useFilterSaleEmployeeDomesticOrderLazyQuery();

  const [
    getRequireAttentionMaterial,
    {
      data: fetchRequireAttentionMaterial,
      loading: loadingRequireAttentionMaterial
    }
  ] = useFilterRequireAttentionMaterialLazyQuery();

  const { data: orderTypesList } = useListOrderTypesQuery();
  const orderTypeList = useMemo(() => {
    const typesList = getOrderTypes(
      orderTypesList?.listOrderTypes,
      formik.values.bu
    ).map(type => ({
      ...type,
      label: intl.formatMessage(
        { defaultMessage: "{label}" },
        { label: type.label }
      )
    }));
    formik.setFieldValue(
      "orderType",
      typesList.length === 1 ? typesList[0]?.value : "All"
    );
    return typesList;
  }, [formik?.values?.bu, orderTypesList?.listOrderTypes]);

  const { data: sourceOfApp } = useListSourceOfAppQuery();
  const sourceOfAppListChangeDomestic = useMemo(
    () =>
      sourceOfApp?.listSourceOfApp.map(({ value }) => ({
        label: value,
        value
      })),
    [sourceOfApp?.listSourceOfApp]
  );
  const ArrowIconComponent = useMemo(
    () => (isOpen ? KeyboardArrowUp : KeyboardArrowDown),
    [isOpen]
  );

  const handleCleanForm = useCallback(() => {
    setIsClean(true);
    handleChangeFilter({});
    setTimeout(() => {
      formik.resetForm();
      setIsClean(false);
    });
  }, []);

  const productGroupList = useMemo(() => {
    if (formik.values.bu === "CIP") {
      formik.setFieldValue("materialGroup1", "");
      return [];
    }
    formik.setFieldValue("materialGroup1", "All");
    return getProductGroup(
      productGroup,
      intl.formatMessage({ defaultMessage: "All" })
    );
  }, [formik.values.bu, productGroup]);

  const buList = useMemo(
    () =>
      dataScgpMe?.scgpMe?.extendData?.scgpBus?.map(bu => ({
        label: bu.name,
        value: bu.name
      })),
    [dataScgpMe?.scgpMe?.extendData?.scgpBus]
  );

  const saleOrgList = useMemo(() => {
    const orgList =
      dataScgpMe?.scgpMe?.extendData?.salesOrgByBu
        ?.find(org => org?.businessUnit === formik?.values?.bu)
        ?.salesOrganizations?.map(e => ({
          label: `${e.code} - ${e.shortName || ""}`,
          value: e.code
        })) || [];
    return orgList;
  }, [dataScgpMe?.scgpMe?.extendData?.salesOrgByBu, formik?.values?.bu]);

  const saleGroupList = useMemo(() => {
    const options = (dataScgpMe?.scgpMe?.extendData?.scgpSalesGroups || []).map(
      item => ({
        label: `${item?.code} - ${item?.name}`,
        value: item?.code
      })
    );
    options.unshift({ label: "ALL", value: "All" });
    return uniqBy(options, "value");
  }, [dataScgpMe?.scgpMe?.extendData?.scgpSalesGroups]);

  useEffect(() => {
    const extendData = dataScgpMe?.scgpMe?.extendData;
    const bu = formik?.values?.bu || extendData?.defaultBusinessUnit?.name;

    const salesOrgByBuAndUser =
      extendData?.salesOrgByBuAndUser?.find(org => org?.businessUnit === bu)
        ?.salesOrganizations || [];

    formik.setValues(prev => ({
      ...prev,
      bu,
      company:
        salesOrgByBuAndUser.length === 1 ? salesOrgByBuAndUser[0].code : ""
    }));
  }, [
    dataScgpMe?.scgpMe?.extendData?.defaultBusinessUnit?.name,
    formik?.values?.bu
  ]);

  const formData = useMemo(
    () => [
      {
        title: intl.formatMessage({ defaultMessage: "Sold to" }),
        name: "soldTo",
        placeholder: intl.formatMessage({ defaultMessage: "ค้นหา Sold to" }),
        type: FIELD_TYPE.SUGGESTION_INPUT,
        md: 6,
        sm: 6
      },
      {
        title: intl.formatMessage({
          defaultMessage: "Material Code/Description"
        }),
        name: "materialCodeName",
        placeholder: intl.formatMessage({
          defaultMessage: "ค้นหา Material Code/Description"
        }),
        type: FIELD_TYPE.SUGGESTION_INPUT,
        md: 6,
        sm: 6
      },
      {
        title: intl.formatMessage({ defaultMessage: "PO No." }),
        name: "poNo",
        placeholder: intl.formatMessage({ defaultMessage: "ระบุ PO No." }),
        type: FIELD_TYPE.SINGLE_INPUT
      },
      {
        title: intl.formatMessage({ defaultMessage: "SO No." }),
        name: "soNo",
        placeholder: intl.formatMessage({ defaultMessage: "ระบุ SO No." }),
        type: FIELD_TYPE.SINGLE_INPUT,
        maxLength: 10
      },
      {
        title: intl.formatMessage({ defaultMessage: "Contract No." }),
        name: "contractNo",
        placeholder: intl.formatMessage({
          defaultMessage: "ระบุ Contract No."
        }),
        type: FIELD_TYPE.SINGLE_INPUT,
        maxLength: 10
      },
      {
        title: intl.formatMessage({ defaultMessage: "DP No." }),
        name: "dpNo",
        placeholder: intl.formatMessage({ defaultMessage: "ระบุ D/P No." }),
        type: FIELD_TYPE.SINGLE_INPUT,
        maxLength: 10
      },
      {
        title: intl.formatMessage({
          defaultMessage: "Invoice No. (Billing No.)"
        }),
        name: "invoiceNo",
        placeholder: intl.formatMessage({
          defaultMessage: "ระบุ Invoice No. (Billing No.)"
        }),
        type: FIELD_TYPE.SINGLE_INPUT,
        maxLength: 10
      },
      {
        title: intl.formatMessage({ defaultMessage: "BU" }),
        name: "bu",
        placeholder: intl.formatMessage({ defaultMessage: "เลือก BU" }),
        type: FIELD_TYPE.SINGLE_SELECT,
        options: buList || []
      },
      {
        title: intl.formatMessage({ defaultMessage: "Sales Org." }),
        name: "company",
        placeholder: intl.formatMessage({ defaultMessage: "เลือก Sales Org." }),
        type: FIELD_TYPE.SINGLE_SELECT,
        options: saleOrgList,
        required: !formik.values.soNo && !formik.values.poNo
      },
      {
        title: intl.formatMessage({ defaultMessage: "Sale Group" }),
        name: "saleGroup",
        placeholder: intl.formatMessage({
          defaultMessage: "เลือก Sale Group"
        }),
        type: FIELD_TYPE.SINGLE_SELECT,
        options: saleGroupList
      },
      {
        title: intl.formatMessage({ defaultMessage: "Sale Employee" }),
        name: "saleEmployee",
        placeholder: intl.formatMessage({
          defaultMessage: "ระบุรหัส Sale Employee"
        }),
        type: FIELD_TYPE.SUGGESTION_INPUT
      },
      {
        title: intl.formatMessage({ defaultMessage: "Order Type" }),
        name: "orderType",
        placeholder: intl.formatMessage({
          defaultMessage: "เลือก Order Type"
        }),
        type: FIELD_TYPE.SINGLE_SELECT,
        options: orderTypeList || []
      },
      {
        title: intl.formatMessage({ defaultMessage: "Date Range" }),
        name: "dateSelectionType",
        options: [
          {
            label: intl.formatMessage({ defaultMessage: "Create Date" }),
            value: DateSelectionType.CREATE_DATE
          },
          {
            label: intl.formatMessage({ defaultMessage: "Last Update Date" }),
            value: DateSelectionType.LAST_UPDATE
          }
        ],
        type: FIELD_TYPE.RADIO_GROUP
      },
      {
        title: intl.formatMessage({ defaultMessage: "Start Date" }),
        name: "gte",
        type: FIELD_TYPE.DATEPICKER,
        required:
          !formik.values.soNo &&
          !formik.values.contractNo &&
          !formik.values.dpNo &&
          !formik.values.invoiceNo &&
          !formik.values.poNo
      },
      {
        title: intl.formatMessage({ defaultMessage: "End Date" }),
        name: "lte",
        type: FIELD_TYPE.DATEPICKER,
        required:
          !formik.values.soNo &&
          !formik.values.contractNo &&
          !formik.values.dpNo &&
          !formik.values.invoiceNo &&
          !formik.values.poNo
      },
      {
        title: intl.formatMessage({ defaultMessage: "Product Group" }),
        name: "materialGroup1",
        placeholder: intl.formatMessage({
          defaultMessage: "เลือก Product Group"
        }),
        type: FIELD_TYPE.SINGLE_SELECT,
        options: productGroupList,
        disabled: formik.values.bu === "CIP"
      },
      {
        title: intl.formatMessage({ defaultMessage: "Order Status (SAP)" }),
        name: "statusSAP",
        placeholder: intl.formatMessage({
          defaultMessage: "เลือก Order Status (SAP)"
        }),
        type: FIELD_TYPE.SINGLE_SELECT,
        options: orderTypeChoices
      },
      {
        title: intl.formatMessage({ defaultMessage: "Source of App" }),
        name: "sourceOfApp",
        placeholder: intl.formatMessage({
          defaultMessage: "เลือก Source of App"
        }),
        type: FIELD_TYPE.SINGLE_SELECT,
        options: sourceOfAppListChangeDomestic || []
      }
    ],
    [
      productGroupList,
      saleOrgList,
      saleGroupList,
      buList,
      formik.values.soNo,
      formik.values.contractNo,
      formik.values.dpNo,
      formik.values.invoiceNo,
      formik.values.poNo,
      sourceOfAppListChangeDomestic
    ]
  );

  const orderStatus = [
    {
      label: "Received Order",
      value: ScgpExportOrderStatus.RECEIVED_ORDER,
      color: "#A3AED0"
    },
    {
      label: "Credit/Cash Issue",
      value: ScgpExportOrderStatus.CREDIT_CASH_ISSUE,
      color: "#FFC107"
    },
    {
      label: "Partial Committed Order",
      value: ScgpExportOrderStatus.PARTIAL_COMMITTED_ORDER,
      color: "#3860BE"
    },
    {
      label: "Ready for delivery",
      value: ScgpExportOrderStatus.READY_FOR_DELIVERY,
      color: "#007BFF"
    },
    {
      label: "Partial Delivery",
      value: ScgpExportOrderStatus.PARTIAL_DELIVERY,
      color: "#009FB8"
    },
    {
      label: "Completed Delivery ",
      value: ScgpExportOrderStatus.COMPLETED_DELIVERY,
      color: "#6CC04A"
    },
    {
      label: "Cancelled ",
      value: ScgpExportOrderStatus.CANCELLED,
      color: "#E41B17"
    },
    {
      label: "Completed Order ",
      value: ScgpExportOrderStatus.COMPLETED_ORDER,
      color: "#1E7D34"
    }
  ];

  const setDataFormik = data => {
    formik.setValues(data);
  };

  return useMemo(
    () => ({
      formik,
      setDataFormik,
      isOpen,
      setIsOpen,
      isClean,
      setIsClean,
      fetchSoldToDataSuggestions,
      loadingSoldToDataSuggestion,
      getRequireAttentionMaterial,
      fetchRequireAttentionMaterial,
      loadingRequireAttentionMaterial,
      getSoldToDataSuggestions,
      ArrowIconComponent,
      handleCleanForm,
      formData,
      orderStatus,
      getSaleEmployeeDataSuggestions,
      fetchSaleEmployeeDataSuggestions,
      loadingSaleEmployeeDataSuggestion
    }),
    [
      formik,
      isOpen,
      setIsOpen,
      isClean,
      setIsClean,
      fetchSoldToDataSuggestions,
      loadingSoldToDataSuggestion,
      getRequireAttentionMaterial,
      loadingRequireAttentionMaterial,
      fetchRequireAttentionMaterial,
      getSoldToDataSuggestions,
      ArrowIconComponent,
      handleCleanForm,
      formData,
      orderStatus,
      getSaleEmployeeDataSuggestions,
      fetchSaleEmployeeDataSuggestions,
      loadingSaleEmployeeDataSuggestion
    ]
  );
};

export default useOrderFilterDomestic;
